import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck } from 'lucide-react';

const LoginForm = ({ onSubmit, error: externalError }) => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    if (externalError) {
      setLoginError(externalError);
    }
  }, [externalError]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }

    if (loginError) {
      setLoginError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      // Error handled by parent component.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-card rounded-[1.75rem] p-6 sm:p-8">
      <div className="mb-6">
        <div className="section-kicker mb-4">
          <ShieldCheck className="h-4 w-4" />
          Welcome back
        </div>
        <h2 className="text-2xl font-bold text-slate-900">Log in to your workspace</h2>
        <p className="mt-2 text-sm text-slate-500">
          Continue to your campaigns, creator matches, and account activity.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-2 block text-sm font-medium text-slate-700">Email</label>
          <input
            name="email"
            type="email"
            placeholder="you@example.com"
            value={formData.email}
            onChange={handleChange}
            className={`input-surface ${errors.email ? '!border-red-400 !shadow-[0_0_0_4px_rgba(248,113,113,0.12)]' : ''}`}
            disabled={isSubmitting}
          />
          {errors.email && <div className="text-red-500 text-sm mt-1">{errors.email}</div>}
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-slate-700">Password</label>
          <input
            name="password"
            type="password"
            placeholder="Enter your password"
            value={formData.password}
            onChange={handleChange}
            className={`input-surface ${errors.password || loginError ? '!border-red-400 !shadow-[0_0_0_4px_rgba(248,113,113,0.12)]' : ''}`}
            disabled={isSubmitting}
          />
          {errors.password && <div className="text-red-500 text-sm mt-1">{errors.password}</div>}
          {loginError && <div className="text-red-500 text-sm mt-1">{loginError}</div>}
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">Secure role-based access</span>
          <Link
            to="/forgot-password"
            className="text-collaborb-band4 hover:text-collaborb-spherical-mid hover:underline"
          >
            Forgot your password?
          </Link>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className={`primary-button !w-full !justify-center ${
            isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
          }`}
        >
          {isSubmitting ? 'Logging in...' : 'Log in'}
          {!isSubmitting && <ArrowRight className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
};

export default LoginForm;
